import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../utils/services/authService';
import { hasBrownlowAccess } from '../config/access';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Map a decoded JWT payload to the app's user object. */
const userFromPayload = (payload) => ({
  userId: payload.userId,
  email: payload.email,
  roles: payload.roles || [],
  subscriptions: payload.subscriptions || [],
});

// setTimeout silently fires immediately past this delay, so long-lived tokens
// are watched in chunks rather than one oversized timer.
const MAX_TIMEOUT = 2 ** 31 - 1;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [watchdogTick, setWatchdogTick] = useState(0);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setExpiresAt(null);
  }, []);

  const applyPayload = useCallback((payload) => {
    setUser(userFromPayload(payload));
    setIsAuthenticated(true);
    setExpiresAt(typeof payload.exp === 'number' ? payload.exp : null);
  }, []);

  // Rehydrate from localStorage on mount. Until this finishes isLoading stays
  // true, so route guards must not redirect on !isAuthenticated before then.
  useEffect(() => {
    const payload = authService.isTokenValid() ? authService.decodeToken() : null;
    if (payload) {
      applyPayload(payload);
    } else if (authService.getToken()) {
      authService.logout();
    }
    setIsLoading(false);
  }, [applyPayload]);

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await authService.login(email, password);

        if (response.success && response.data?.success && response.data?.token) {
          authService.setToken(response.data.token);
          const payload = authService.decodeToken();

          if (payload) {
            applyPayload(payload);
            const userData = userFromPayload(payload);
            return {
              success: true,
              message: response.data.message || 'Login successful',
              roles: userData.roles,
              subscriptions: userData.subscriptions,
            };
          }

          // Token came back unreadable — don't leave a half-signed-in session.
          authService.logout();
          return { success: false, error: 'Received an invalid session token. Please try again.' };
        }

        return {
          success: false,
          error: response.message || 'Login failed. Please check your credentials.',
        };
      } catch (error) {
        return {
          success: false,
          error: 'An unexpected error occurred. Please try again.',
        };
      }
    },
    [applyPayload]
  );

  const hasRole = useCallback(
    (role) => !!user?.roles?.includes(role),
    [user]
  );

  const hasSubscription = useCallback(
    (subscriptionKey) => !!user?.subscriptions?.includes(subscriptionKey),
    [user]
  );

  // Hydrate the session from an existing token (e.g. after free-tier
  // registration) without making a second login API call.
  const loginWithToken = useCallback(
    (token) => {
      authService.setToken(token);
      const payload = authService.decodeToken();
      if (!payload) {
        authService.logout();
        return false;
      }
      applyPayload(payload);
      return true;
    },
    [applyPayload]
  );

  // Brownlow Genius entitlement: any of ACCESS_ROLES across roles/subscriptions.
  const hasAccess = useCallback(() => hasBrownlowAccess(user), [user]);

  // Expiry watchdog. Fires at the token's actual exp instead of polling, and
  // flags the reason so the login screen can explain the bounce.
  useEffect(() => {
    if (!isAuthenticated || !expiresAt) return undefined;

    const remaining = expiresAt * 1000 - Date.now();
    if (remaining <= 0) {
      sessionStorage.setItem('sessionExpired', 'true');
      logout();
      return undefined;
    }

    const timer = setTimeout(() => {
      if (authService.isTokenValid()) {
        setWatchdogTick((tick) => tick + 1);
        return;
      }
      sessionStorage.setItem('sessionExpired', 'true');
      logout();
    }, Math.min(remaining, MAX_TIMEOUT));

    return () => clearTimeout(timer);
  }, [isAuthenticated, expiresAt, watchdogTick, logout]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithToken,
    logout,
    hasRole,
    hasSubscription,
    hasAccess,
    // Existing data fetchers read the raw token through this accessor.
    token: () => authService.getToken(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
